import { NavLink, useLocation } from 'react-router-dom';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { UserRole, OPERATIONS_ROLES, BUSINESS_SHEET_VIEW_ROLES } from '@bhops/shared';
import { Icon, type IconName } from '@/components/Icon';
import {
  PARTS_SUPPLIERS_ROUTE_ROLES,
  RETIRE_CANDIDATES_ROUTE_ROLES,
  roleCanAccessRoute,
} from '@/permissions';
// 개선 9건 차수1(26-08-04) §4 — 서명관리 열람 역할 정본(router.tsx 와 공유).
import { SIGN_DOCUMENTS_VIEW_ROLES } from '@/pages/sign/sign-access';
// 미사용 메뉴 숨김 — 권한이 아닌 축이다(오너 결정 2026-08-28). nav-hidden.ts 참조.
import { isNavHidden } from './nav-hidden';
import { SECTION_ACCENT, sectionFill } from './section-accent';

// Kuun UI 통합안(After) — 사이드바.
//   - 활성 항목 = --brand 채움 + --brand-on + --shadow-brand + radius(--r-btn).
//   - 각 항목 좌측에 Icon(인라인 SVG, 이모지 금지). 그룹 라벨(BUSINESS 등)은
//     클릭 가능한 NavLink(팀 대시보드) 유지.
//   - 로고는 현재 사용 중인 /logo-monogram.svg(BH) 그대로 — 핸드오프의 K 목업
//     (assets/logo-monogram.svg)은 쓰지 않는다.
//   - 2026-10-23 오너 "이 메뉴도 접었다 펼 수 있게 하자. 기본적으론 닫혀있고, 로고를 누르면 펼쳐지는
//     형식으로" — 데스크톱은 아이콘만 보이는 좁은 띠(RAIL_WIDTH)로 시작하고, 로고를 누르면 펼치고 다시
//     누르면 접는다. 홈 이동은 펼쳤을 때의 'BH Console' 글자로 옮겼다. 접혀도 메뉴 이름은 화면 읽기 ·
//     마우스를 올렸을 때(title) 보인다. 모바일 드로어(onNavigate)는 늘 펼친 모양.

interface Props {
  role: UserRole;
  // 모바일 드로어에서 항목을 누르면 드로어를 닫기 위한 콜백(데스크톱은 미전달).
  onNavigate?: () => void;
}

// 2026-10-20 오너 — 왼쪽 여백을 줄였다(메뉴 영역 왼쪽 10→6, 버튼 안 12→10, 로고·상태줄 16→12).
// 같은 날 폭도 212→188 로 줄였다가 "이쪽(오른쪽) 여백 너무 좁아졌네. 다시 원복하자" 로 폭과
// 오른쪽 여백(10)은 되돌렸다 — 188 이면 로고 글자가 오른쪽 끝에 닿고 스크롤바가 버튼에 붙었다.
const WIDTH = 212;
const RAIL_WIDTH = 60;
const wrap: CSSProperties = {
  width: WIDTH,
  flexShrink: 0,
  transition: 'width 160ms ease',
  overflow: 'hidden',
  background: 'var(--bg-surface)',
  borderRight: '1px solid var(--border-subtle)',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};
const brand: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '16px 12px 12px',
  textDecoration: 'none',
  color: 'inherit',
};
const navScroll: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '0 10px 8px 6px',
};
const logoBtn: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  flexShrink: 0,
  padding: 0,
  border: 'none',
  background: 'transparent',
  borderRadius: 'var(--r-btn)',
  cursor: 'pointer',
};
/** 아직 안 눌러 본 로고의 빨간 점(패치노트 버튼과 같은 모양) */
const hintDot: CSSProperties = {
  position: 'absolute',
  top: -3,
  right: -3,
  width: 9,
  height: 9,
  borderRadius: '50%',
  background: 'var(--fg-danger)',
  border: '2px solid var(--bg-surface)',
};
const LOGO_TRIED_KEY = 'kuun:sidebar-logo:tried';
function readLogoTried(): boolean {
  try {
    return window.localStorage.getItem(LOGO_TRIED_KEY) === '1';
  } catch {
    return false;
  }
}
function markLogoTried(): void {
  try {
    window.localStorage.setItem(LOGO_TRIED_KEY, '1');
  } catch {
    /* 사생활 보호 모드 등 — 다음에 또 깜빡일 뿐 */
  }
}
/** 접힌 띠에서 글자를 숨기되 화면 읽기 · 찾기에는 남긴다 */
const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};
// 2026-10-19 오너 — "비즈니스 / 매뉴팩처링 / 필드 구분감이 너무 안 좋다. 선을 긋든 색을 다르게
// 하든". 두 번째 그룹부터 위에 구분선, 그룹 이름은 진하고 굵게 + 앞에 브랜드 색 막대.
const group: CSSProperties = { marginBottom: 2 };
const groupDivided: CSSProperties = {
  ...group,
  marginTop: 8,
  paddingTop: 4,
  borderTop: '1px solid var(--border-subtle)',
};
const groupLblBar: CSSProperties = {
  width: 3,
  height: 12,
  borderRadius: 2,
  flexShrink: 0,
};
const groupLblBase: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: '12px 10px 6px',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  borderRadius: 'var(--r-btn)',
};
const footer: CSSProperties = {
  marginTop: 'auto',
  padding: '12px 12px',
  borderTop: '1px solid var(--border-subtle)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-muted)',
};

// 배포 버전 라인(빌드 시 주입) — API 상태 아래 작게 mono 로.
const versionLine: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '-0.01em',
  color: 'var(--fg-subtle)',
  fontVariantNumeric: 'tabular-nums',
};

// 2026-10-19 오너 — 고른 메뉴는 늘 초록이 아니라 **그 묶음의 띠 색**으로 칠한다. 주황 같은 밝은 띠
// 위에서도 흰 글씨가 읽히게 조금 어둡게 섞는다.
function itemStyle(active: boolean, accent: string): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 10px',
    marginBottom: 2,
    borderRadius: 'var(--r-btn)',
    textDecoration: 'none',
    fontSize: 14,
    color: active ? '#fff' : 'var(--fg-muted)',
    background: active ? sectionFill(accent) : 'transparent',
    boxShadow: active ? `0 2px 8px color-mix(in srgb, ${accent} 35%, transparent)` : 'none',
    fontWeight: active ? 600 : 500,
  };
}

/** `alsoActiveFor` 판정 — 화면 안 탭으로 갈라진 형제 경로를 같은 항목으로 본다. */
function isAlsoActive(item: NavItem, pathname: string): boolean {
  return !!item.alsoActiveFor?.includes(pathname);
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  badge?: ReactNode;
  end?: boolean;
  /**
   * 이 경로들에 있어도 **이 항목을 활성으로 칠한다.**
   *
   * 한 메뉴 항목이 화면 안에서 탭으로 갈라질 때 필요하다 — 탭을 넘기면 주소가
   * 바뀌는데(링크 공유·뒤로가기를 살리려고) 메뉴는 같은 자리여야 한다.
   * 없으면 사용자가 섹션 안에 있는데 메뉴가 아무것도 안 고른 것처럼 보인다.
   */
  alsoActiveFor?: string[];
  /**
   * 이 항목이 보일 역할. router.tsx 의 ProtectedRoute requiredRole(s) 를 미러링한다.
   * 미지정 = 전원 노출(현행 동작). ADMIN 은 항상 통과(roleCanAccessRoute 참조).
   * ⚠ 숨김은 막다른길(dead-end) 제거용 UX — 실제 차단은 ProtectedRoute 가 담당.
   */
  allowedRoles?: ReadonlyArray<UserRole>;
}

interface NavGroupConfig {
  label: string;
  /** 그룹 이름 앞 막대 색 — 묶음마다 달라 한눈에 갈린다(2026-10-19 오너). */
  accent: string;
  // 그룹 이름은 글자일 뿐 누르는 자리가 아니다. 종전엔 이름이 그 묶음 대시보드 링크였는데 아무도 몰랐다 —
  // 대시보드는 묶음 맨 위 항목으로 따로 둔다(2026-10-28 오너 "저기는 그냥 기능 빼버리고 대시보드 탭 다 따로 두자").
  items: NavItem[];
}

function NavGroup({
  config,
  pathname,
  role,
  divided = false,
  rail = false,
}: {
  config: NavGroupConfig;
  pathname: string;
  role: UserRole;
  /** 위에 구분선을 긋는다(첫 그룹 말고 모두). */
  divided?: boolean;
  /** 접힌 띠 — 아이콘만, 그룹 이름은 색 막대만 */
  rail?: boolean;
}) {
  // 두 겹으로 걸러낸다. 순서가 뜻을 가진다:
  //   ① 미사용 숨김(isNavHidden) — 역할과 무관하게 **전원**에게서 숨는다.
  //      ADMIN 은 아래 역할 판정을 항상 통과하므로, 이 겹이 먼저여야 관리자에게도
  //      숨는다(nav-hidden.ts 의 설명 참조).
  //   ② 역할이 접근 가능한 항목만 — 권한 없는 탭은 막다른길이 되므로 숨긴다.
  const visibleItems = config.items.filter(
    (item) => !isNavHidden(item.to) && roleCanAccessRoute(role, item.allowedRoles),
  );
  // 그룹 내 보이는 항목이 하나도 없으면 그룹 자체(라벨 포함)를 숨긴다.
  if (visibleItems.length === 0) return null;
  const lblStyle: CSSProperties = rail
    ? { ...groupLblBase, justifyContent: 'center', padding: '10px 0 6px' }
    : groupLblBase;
  const bar: CSSProperties = rail ? { ...groupLblBar, width: 18, height: 3 } : groupLblBar;
  const label = rail ? <span style={srOnly}>{config.label}</span> : config.label;
  return (
    <div style={divided ? groupDivided : group} data-testid="nav-group">
      <div
        style={{ ...lblStyle, color: 'var(--fg-default)' }}
        title={rail ? config.label : undefined}
      >
        <span aria-hidden style={{ ...bar, background: config.accent }} />
        {label}
      </div>
      {visibleItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          title={rail ? item.label : undefined}
          style={({ isActive }) => ({
            ...itemStyle(isActive || isAlsoActive(item, pathname), config.accent),
            ...(rail ? { justifyContent: 'center', padding: '9px 0', position: 'relative' } : null),
          })}
        >
          {({ isActive }) => (
            <>
              <Icon
                name={item.icon}
                size={18}
                color={isActive || isAlsoActive(item, pathname) ? '#fff' : 'var(--fg-subtle)'}
              />
              <span style={rail ? srOnly : undefined}>{item.label}</span>
              {item.badge != null && !rail && (
                <span style={{ marginLeft: 'auto' }}>{item.badge}</span>
              )}
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}

export function Sidebar({ role, onNavigate }: Props) {
  const intl = useIntl();
  const location = useLocation();
  const t = (id: string) => intl.formatMessage({ id });
  // 데스크톱은 접힌 띠로 시작 — 로고로 펼친다. 화면을 옮겨도 사이드바는 그대로라 상태가 남는다.
  const [open, setOpen] = useState(false);
  const rail = !onNavigate && !open;
  // 로고를 한 번도 안 눌러 본 사람에게는 로고가 패치노트처럼 깜빡인다(2026-10-23 오너 "눌러볼 수 있게 유도").
  const [logoTried, setLogoTried] = useState(readLogoTried);
  function toggleOpen() {
    setOpen((o) => !o);
    if (!logoTried) {
      markLogoTried();
      setLogoTried(true);
    }
  }

  const healthQ = useQuery({
    queryKey: ['health'],
    // R2 후속(워크플로 M11) — apiClient 와 동일한 base URL 규칙(VITE_API_BASE_URL) 을
    // 따른다. 하드코딩 '/api/v1' 는 API 가 별도 포트(:4000)인 dev 에서 빗나가 푸터
    // 'API 연결' 점이 항상 끊김(빨강)으로 오표시됐다.
    queryFn: () =>
      fetch(`${import.meta.env.VITE_API_BASE_URL ?? '/api/v1'}/health`).then((r) => r.ok),
    refetchInterval: 60_000,
    retry: false,
  });
  const apiOk = healthQ.data !== false;

  const business: NavGroupConfig = {
    label: t('nav.group.business'),
    accent: SECTION_ACCENT.business,
    items: [
      // 묶음 대시보드(2026-10-28 오너 — 그룹 이름 링크 대신 따로 둔 항목). end: 하위 화면에서는 칠하지 않는다.
      { to: '/business', label: '사업 대시보드', icon: 'grid', end: true },
      // round9 (요구 1) — 국내/해외 사이드바 2항목을 단일 진입점으로 되돌리고,
      // 국내·해외 전환은 CustomerMasterPage 헤더의 페이지 내 탭으로 이동했다
      // (사이드바 하위 2계층 진입이 불편하다는 피드백).
      { to: '/business/master', label: t('nav.item.customer_master'), icon: 'building' },
      // 🚫 **'계약' 항목을 여기에 다시 넣지 마라.** §Phase 3(26-05-26) 에서 영업 그룹을
      //   '고객사 마스터' 단일 진입점으로 정리한 오너 결정이고, 그 결정을 지키는 시험이
      //   Sidebar.test.tsx 에 있다("does NOT render flattened sub-domain links").
      //
      //   26-08-04 에 내가 그것을 되살렸다 — "계약이 2건 이상인 고객사를 볼 곳이 없다"는
      //   이유였는데, **그 시험을 통과시키려고 시험에서 '계약'을 빼는 방식**이었다.
      //   그물을 자르고 결정을 뒤집은 것이다. 26-08-05 오너 지적으로 되돌렸다.
      //
      //   그때의 문제의식 자체는 이미 다른 방법으로 해결됐다: 고객사 상세가 계약을
      //   **계약별 카드로 전부** 그리고(차수2e), 계약 목록 화면은 라우트로 계속 살아 있어
      //   고객사 상세의 '계약 목록 →' 링크와 주소 입력으로 도달한다. 사이드바 항목이
      //   없어도 계약이 숨지 않는다.
      {
        to: '/business/documents',
        label: t('nav.item.customer_documents'),
        icon: 'file',
        // router.tsx L128 ProtectedRoute requiredRoles 미러링.
        // 권한 통합(오너 결정 2026-08-27) — router.tsx 의 requiredRoles 와 같은 상수.
        allowedRoles: OPERATIONS_ROLES,
      },
      // 2026-10-23 오너 — "business에서 고객사 인바운드 현황, 데모 계약 전환 탭을 만들거야".
      //   구글 시트 '인바운드 문의 현황'의 '자동 IB(YY.MM)' 달 탭(읽기 전용)과 '데모-계약 전환' 탭(양방향).
      //   시트 화면 셋은 제조팀이 못 본다(오너 2026-10-23) — router.tsx 와 같은 BUSINESS_SHEET_VIEW_ROLES.
      {
        to: '/business/inbound',
        label: t('nav.item.inbound'),
        icon: 'mail',
        allowedRoles: BUSINESS_SHEET_VIEW_ROLES,
      },
      // 2026-10-23 오너 "도입 문의 현황은 안만들었네?" — '도입문의 현황 (YYYY_M) 수기' 달 탭(양방향).
      {
        to: '/business/inquiry',
        label: t('nav.item.inquiry'),
        icon: 'phone',
        allowedRoles: BUSINESS_SHEET_VIEW_ROLES,
      },
      {
        to: '/business/demo-conversion',
        label: t('nav.item.demo_conversion'),
        icon: 'list-check',
        allowedRoles: BUSINESS_SHEET_VIEW_ROLES,
      },
    ],
  };
  const manufacturing: NavGroupConfig = {
    label: t('nav.group.manufacturing'),
    accent: SECTION_ACCENT.manufacturing,
    items: [
      // 묶음 대시보드(2026-10-28 오너 — 그룹 이름 링크 대신 따로 둔 항목). end: 하위 화면에서는 칠하지 않는다.
      { to: '/manufacturing', label: '제조 대시보드', icon: 'grid', end: true },
      { to: '/manufacturing/robots', label: t('nav.item.robots'), icon: 'bot' },
      {
        to: '/manufacturing/production-plan',
        label: t('nav.item.production_plan'),
        icon: 'factory',
      },
      { to: '/manufacturing/assignments', label: t('nav.item.assignments'), icon: 'package' },
      { to: '/manufacturing/inventory', label: '재고 현황', icon: 'list' },
      { to: '/manufacturing/ecns', label: '변경점(ECN)', icon: 'scroll' },
      // docs/plan/loader-history-traceability-2026-07 — 로더(로봇 팔) 이력·역추적.
      // §9 — wrench 는 유지보수(서비스 오더) 전용. 로더는 robot-arm.
      { to: '/manufacturing/loaders', label: '로더 이력', icon: 'robot-arm' },
      // windows-ux-edit (2026-06, 슬라이드5) — 부품 카탈로그/부품 LOT/공급사 nav 제거
      // (미사용). 라우트/페이지는 보존(직접 URL 동작). 라우트 완전 제거는 후속
      // (FleetTrackerPage 의 '/parts/lots' 링크 처리 동반 필요).
      //
      // 오너 확정 2026-08-20 결정 4 로 복원(제조팀 권한 요구 — 계획서 요구 1).
      // 위 2026-06 제거와 겹친 결정 이력이라 그 주석은 지우지 않는다. 노출은
      // ADMIN·레거시 MANAGER·MFG_MANAGER 만 — router.tsx 의 ProtectedRoute 와
      // **같은 상수**(PARTS_SUPPLIERS_ROUTE_ROLES)를 공유해 미러링 표류를 원천
      // 차단한다(서명 관리의 SIGN_DOCUMENTS_VIEW_ROLES 와 같은 방식).
      // 아이콘: 부품 = component(정본 표), 입고 = truck(정본 표의 '물류' 계열),
      // 공급사 = building 은 고객사 마스터와 그림이 겹친다 — icon-vocabulary.md
      // '알려진 예외' 에 등록, 다음 아이콘 정리 차수에 함께 푼다.
      //
      // ⚠ 2026-08-28 현재 이 세 항목은 **화면에 나오지 않는다.**
      //   오너 결정: "부품 카탈로그, 부품 입고, 공급사 탭은 모두에게 숨겨주세요.
      //   당장은 사용하지 않는 탭입니다." → 위 2026-08-20 결정 4 를 뒤집는다.
      //   숨김 판정은 `nav-hidden.ts` 의 NAV_HIDDEN_ROUTES 한 곳에 있다(NavGroup
      //   필터 참조). 항목 정의와 라우트를 남겨 둔 것은 "당장은" 이라는 말 때문이다
      //   — 되살리려면 그 목록에서 경로만 지우면 된다.
      //   allowedRoles 는 손대지 않았다: 라우트 게이트가 계속 쓰는 값이고,
      //   숨김은 권한 축이 아니다(역할 목록을 비우면 오히려 전원 노출이 된다).
      {
        to: '/parts/catalog',
        label: '부품 카탈로그',
        icon: 'component',
        allowedRoles: PARTS_SUPPLIERS_ROUTE_ROLES,
      },
      {
        to: '/parts/lots',
        label: '부품 입고',
        icon: 'truck',
        allowedRoles: PARTS_SUPPLIERS_ROUTE_ROLES,
      },
      {
        to: '/suppliers',
        label: '공급사',
        icon: 'building',
        allowedRoles: PARTS_SUPPLIERS_ROUTE_ROLES,
      },
    ],
  };
  const field: NavGroupConfig = {
    label: t('nav.group.field'),
    accent: SECTION_ACCENT.field,
    items: [
      // 묶음 대시보드(2026-10-28 오너 — 그룹 이름 링크 대신 따로 둔 항목). end: 하위 화면에서는 칠하지 않는다.
      { to: '/field', label: '필드 대시보드', icon: 'grid', end: true },
      // ── 순서는 오너가 정했다(2026-09-16) ─────────────────────────────────
      // 매일 쓰는 것이 위, 드물게 보는 것이 아래다. 서비스 오더가 맨 아래로
      // 내려간 건 현장 흐름이 '리포트 → 조치' 로 굳었기 때문이다(그 화면이
      // 1번이다). 이 배열 순서가 곧 화면 순서다 — 바꾸려면 여기만 고친다.
      //
      // round5 #14 — 리포트 인박스는 서비스 오더의 '고객 리포트' 탭으로 통합
      // (같은 인시던트가 두 인박스에 이중으로 뜨던 문제). /reports 는 redirect.
      //
      // 26-09-11 (오너 요청) — 그 통합은 유지한 채, Field 그룹에 독립 항목을
      // **하나 더** 둔다. 서비스 오더를 거치지 않고 리포트만 바로 보고 싶다는
      // 요구다. ⚠ round5 #14 가 없앤 '이중 인박스'가 형태만 달리 돌아온 것이기도
      //   하다 — 같은 리포트가 여기와 서비스 오더 탭 양쪽에 보인다.
      {
        to: '/field/customer-reports',
        // 이 항목 안에 탭이 둘이라(고객사 리포트 · 고장 조치 이력) 한쪽 이름만
        // 걸면 나머지가 메뉴에서 안 보인다. 둘을 같이 적는다(오너 결정 26-09-14).
        label: '리포트 · 조치 이력',
        // 아이콘 교환(오너 결정 2026-09-16) — 종전엔 이쪽이 inbox, 서비스 오더가
        // wrench 였다. 바꾼 쪽이 정본 표의 취지에 더 맞는다: 이 화면의 절반이
        // '고장 조치 이력'(유지보수 기록)이라 wrench 고, 서비스 오더는 실제
        // 수신함(ServiceOrdersInboxPage)이라 inbox 다.
        // 표도 같이 고쳤다 — docs/design/icon-vocabulary.md.
        icon: 'wrench',
        // '고장 조치 이력'은 이 화면의 탭이다. 탭을 넘기면 주소가 바뀌므로
        // (FieldReportTabs) 그 경로에서도 이 항목이 켜져 있어야 한다.
        alsoActiveFor: ['/field/fault-remediations'],
      },
      // '고장 조치 이력'은 메뉴 항목이 아니라 **위 화면 안의 탭**이다
      // (오너 결정 2026-09-14). 신고를 보고 "그래서 뭘 했나" 를 찾는 흐름이라
      // 메뉴를 오갈 일이 아니다. 라우트는 /field/fault-remediations 로 살아 있고
      // FieldReportTabs 가 둘을 잇는다.
      { to: '/infrastructure', label: t('nav.item.infrastructure'), icon: 'truck' },
      {
        to: '/field/sign/documents',
        label: '서명 관리 · 페리페럴',
        icon: 'signature',
        // router.tsx 의 ProtectedRoute requiredRoles 미러링 — 개선 9건 차수1
        // (26-08-04) §4 부터 **같은 상수를 공유**한다(미러링 표류 원천 차단).
        // 제조팀(MFG_MANAGER)은 읽기 전용으로 들어온다(결정 ③A).
        allowedRoles: SIGN_DOCUMENTS_VIEW_ROLES,
      },
      { to: '/field/active-customers', label: '활성 고객사', icon: 'map-pin' },
      { to: '/fleet/tracker', label: t('nav.item.fleet_tracker'), icon: 'eye' },
      // 서비스 오더 — 맨 아래. 화면은 그대로 살아 있고 딥링크·알림도 그대로다
      // (대시보드 KPI 카드와 캘린더가 여기로 들어온다).
      { to: '/service-orders', label: t('nav.item.service_orders'), icon: 'inbox' },
    ],
  };
  // §9 — 정본 표(docs/design/icon-vocabulary.md)와 아직 어긋나는 항목이 남아 있다
  // (DSAR=file, 셀프체크=list, 폐기 후보=refresh). 매일 쓰는 메뉴라 근육기억을
  // 지키려고 이번 차수에는 '로더 이력'·'감사 로그' 2개만 바꿨다 — 문서의
  // '알려진 예외' 절 참조.
  //
  // 오너 확정 2026-08-20 요구 1 ⑤ — '회수 검토'가 제조팀(레거시 MANAGER 포함)에
  // 열리며 그룹 통째 ADMIN 가드를 항목 단위 allowedRoles 로 내렸다. 나머지 항목은
  // 종전과 같은 ADMIN 전용이고, 비관리자에게는 '회수 검토' 하나만 남는다(빈
  // 그룹은 NavGroup 이 라벨째 숨긴다 — 종전 VIEWER 동작 보존).
  const adminOnly: ReadonlyArray<UserRole> = [UserRole.ADMIN];
  const admin: NavGroupConfig = {
    label: t('nav.group.admin'),
    accent: SECTION_ACCENT.admin,
    items: [
      {
        to: '/privacy-requests',
        label: t('nav.item.privacy_requests'),
        icon: 'file',
        allowedRoles: adminOnly,
      },
      // §9 — scroll 은 ECN(변경점) 전용. 감사 로그는 history.
      {
        to: '/audit-logs',
        label: t('nav.item.audit_logs'),
        icon: 'history',
        allowedRoles: adminOnly,
      },
      { to: '/users', label: t('nav.item.users'), icon: 'users', allowedRoles: adminOnly },
      {
        to: '/admin/incomplete-records',
        label: t('nav.item.incomplete_records'),
        icon: 'alert',
        allowedRoles: adminOnly,
      },
      {
        to: '/admin/option-groups',
        label: t('nav.item.option_groups'),
        icon: 'settings',
        allowedRoles: adminOnly,
      },
      {
        to: '/admin/retire-candidates',
        label: t('nav.item.retire_candidates'),
        icon: 'refresh',
        // 요구 1 ⑤ — 서버 RETIRE_CANDIDATE_READ_ROLES 와 쌍. 라우트와 같은
        // 정본 상수를 공유해 미러링 표류를 원천 차단한다.
        allowedRoles: RETIRE_CANDIDATES_ROUTE_ROLES,
      },
      {
        to: '/admin/self-check-logs',
        label: t('nav.item.self_check_logs'),
        icon: 'list',
        allowedRoles: adminOnly,
      },
    ],
  };

  return (
    <aside
      style={{ ...wrap, ...(rail ? { width: RAIL_WIDTH } : null) }}
      className="kuun-no-select"
      data-testid="sidebar"
      data-collapsed={rail || undefined}
    >
      <div
        style={{
          ...brand,
          ...(rail ? { justifyContent: 'center', padding: '16px 0 12px' } : null),
        }}
      >
        {onNavigate ? (
          <NavLink
            to="/"
            aria-label={t('nav.home_label')}
            onClick={onNavigate}
            style={{ display: 'inline-flex' }}
          >
            <img src="/logo-monogram.svg" width={30} height={30} alt="Beyond Honeycomb" />
          </NavLink>
        ) : (
          <button
            type="button"
            onClick={toggleOpen}
            aria-expanded={open}
            aria-label={t(open ? 'nav.collapse' : 'nav.expand')}
            title={t(open ? 'nav.collapse' : 'nav.expand')}
            className={logoTried ? undefined : 'kuun-logo-hint'}
            data-hint={logoTried ? undefined : 'true'}
            style={logoBtn}
          >
            <img src="/logo-monogram.svg" width={30} height={30} alt="Beyond Honeycomb" />
            {!logoTried && <span style={hintDot} aria-hidden />}
          </button>
        )}
        {!rail && (
          <NavLink
            to="/"
            aria-label={t('nav.home_label')}
            onClick={onNavigate}
            style={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap' }}
          >
            {/* windows-ux-edit (2026-06) — '로봇 운영' 부제 제거 + 'BH Console' 확대. */}
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>
              BH Console
            </div>
          </NavLink>
        )}
      </div>

      {/* 모바일 드로어: 네비 링크(또는 홈 로고)를 누르면 이동 후 드로어를 닫는다.
          NavLink 를 개별 배선하지 않고 컨테이너 위임으로 처리(anchor 클릭 캡처). */}
      <div
        style={rail ? { ...navScroll, padding: '0 8px 8px', scrollbarWidth: 'none' } : navScroll}
        onClick={
          onNavigate ? (e) => (e.target as HTMLElement).closest('a') && onNavigate() : undefined
        }
      >
        <NavGroup config={business} pathname={location.pathname} role={role} rail={rail} />
        <NavGroup
          config={manufacturing}
          pathname={location.pathname}
          role={role}
          divided
          rail={rail}
        />
        <NavGroup config={field} pathname={location.pathname} role={role} divided rail={rail} />
        {/* 요구 1 ⑤ — 그룹 통째 ADMIN 가드 대신 항목 allowedRoles 로 거른다.
            보이는 항목이 0개면 NavGroup 이 그룹째 숨겨 종전 동작이 보존된다. */}
        <NavGroup config={admin} pathname={location.pathname} role={role} divided rail={rail} />
      </div>

      <div style={rail ? { ...footer, alignItems: 'center', padding: '12px 0' } : footer}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: apiOk ? 'var(--robot-operating)' : 'var(--severity-urgent)',
              flexShrink: 0,
            }}
            title={apiOk ? '서버 연결됨' : '서버 연결 끊김'}
          />
          <span style={rail ? srOnly : undefined}>{t('nav.api_connected')}</span>
        </div>
        {/* 배포 버전(빌드 시 주입) — 업데이트 후 어떤 빌드가 떠 있는지 확인용. */}
        <span style={rail ? srOnly : versionLine} title={`build ${__BUILD_TIME__} UTC`}>
          v{__APP_VERSION__} · {__BUILD_SHA__}
        </span>
      </div>
    </aside>
  );
}
